import { useState } from "react";
import {
  Typography,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Table,
  Tooltip,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ExpandableText,
  useExpandableRows,
  TableLink,
} from "../../../ReusableComponents/TableHelpers";
import {
  getRowStyles,
  isAlternateRow as checkIsAlternateRow,
} from "../../../../utils/reusableStyles";
import {
  capitalizeWords,
  parseAnchor,
  isValidWebsite,
  getWebsiteValue,
  markLinkClicked,
  getDisplayText,
} from "../../../../utils/functions";
import theme from "../../../../theme";
import {
  BODY_SPACING,
  SPACING,
  COMPONENT_SPACING,
  TEXT_FEATURES,
} from "../../../../utils/constants/designConstants";

const TABLE_HEADERS = [
  "Supplier Name",
  "National Status",
  "Service Type",
  "Accreditations",
  "Turnover",
  "Description",
  "Website",
];

export const SuppliersToShowTable = ({ suppliersToShow }) => {
  const [clickedLinks, setClickedLinks] = useState(new Set());
  const { expandedRows, handleToggle } = useExpandableRows();

  // Check if it's small screen
  const isSmallScreen = useMediaQuery("(max-width:768px)");

  if (!suppliersToShow || suppliersToShow.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: "primary.white",
          py: BODY_SPACING.ERROR_MESSAGE_SECTION_PADDING,
          mt: BODY_SPACING.ERROR_MESSAGE_SECTION_MARGIN_TOP,
          mb: BODY_SPACING.ERROR_MESSAGE_SECTION_MARGIN_BOTTOM,
        }}
      >
        Suppliers aren't available at the moment.
      </Typography>
    );
  }

  try {
    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ border: "none", backgroundColor: "primary.main" }}
      >
        <Table
          sx={{
            minWidth: COMPONENT_SPACING.MIN_TABLE_WIDTH,
            mb: SPACING.HEADER_MARGIN_B,
          }}
          stickyHeader
          aria-label="suppliers table"
          size="small"
        >
          {/* Table Headers */}
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header, cellIndex) => (
                <TableCell
                  key={cellIndex}
                  sx={{
                    backgroundColor: "transparent",
                    border: "none",
                    color: "primary.brandSecondary",
                    fontWeight: "bold",
                    zIndex: 1,
                    pb: 4,
                    borderTopLeftRadius:
                      cellIndex === 0 ? theme.borderRadius.small : 0,
                    borderTopRightRadius:
                      cellIndex === TABLE_HEADERS.length - 1
                        ? theme.borderRadius.small
                        : 0,
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {suppliersToShow.map((item, index) => {
              const website = getWebsiteValue(item);
              const isWebsiteValid = isValidWebsite(website);
              const parsedTitle = parseAnchor(item["Supplier Name"]);
              const isAlternate = checkIsAlternateRow(index);
              const isExpanded = expandedRows[index];
              const displayText = getDisplayText(
                item.Description,
                TEXT_FEATURES.MAX_DESCRIPTION_LEN,
              );

              return (
                <TableRow key={index} sx={getRowStyles(index, theme)}>
                  {/* Supplier Name */}
                  <TableCell>
                    {parsedTitle ? (
                      <TableLink
                        href={parsedTitle.href}
                        text={capitalizeWords(parsedTitle.text)}
                        clickedLinks={clickedLinks}
                        onLinkClick={(href) =>
                          markLinkClicked(href, clickedLinks, setClickedLinks)
                        }
                        fontWeight={TEXT_FEATURES.LINK_FONT_WEIGHT}
                        visitedDecoration="underline"
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  {/* National Status */}
                  <TableCell>{item["National Status"] || "-"}</TableCell>

                  {/* Service Type */}
                  <TableCell>{item["Service Type"] || "-"}</TableCell>

                  {/* Accreditations */}
                  <TableCell>{item.Accreditations || "-"}</TableCell>

                  {/* Turnover */}
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {item.Turnover || "-"}
                  </TableCell>

                  {/* Description */}
                  <TableCell
                    sx={{
                      minWidth: TEXT_FEATURES.MIN_DESCRIPTION_WIDTH,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {isSmallScreen ? (
                      <ExpandableText
                        text={item.Description}
                        maxLength={TEXT_FEATURES.MAX_DESCRIPTION_LEN}
                        isExpanded={isExpanded}
                        onToggle={() => handleToggle(index)}
                        isAlternateRow={isAlternate}
                      />
                    ) : (
                      <Tooltip title={item.Description || "-"} arrow>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                            maxWidth: "100%",
                          }}
                        >
                          {displayText}
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>

                  {/* Website */}
                  <TableCell
                    sx={{
                      maxWidth: TEXT_FEATURES.MAX_WEBSITE_DISPLAY_LEN,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: isSmallScreen ? "normal" : "nowrap",
                      wordBreak: isSmallScreen ? "break-all" : "normal",
                    }}
                  >
                    {isWebsiteValid ? (
                      <TableLink
                        href={website}
                        text={website}
                        clickedLinks={clickedLinks}
                        onLinkClick={(href) =>
                          markLinkClicked(href, clickedLinks, setClickedLinks)
                        }
                        fontWeight={TEXT_FEATURES.LINK_FONT_WEIGHT}
                        showTooltip={!isSmallScreen}
                        tooltipText={website}
                        visitedDecoration="underline"
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  } catch (error) {
    console.error("Error rendering suppliers table: ", error);
    return (
      <Typography
        variant="body2"
        color="error"
        sx={{ py: BODY_SPACING.ERROR_MESSAGE_SECTION_PADDING }}
      >
        Sorry, we couldn’t display suppliers at the moment.
      </Typography>
    );
  }
};
